import type { TreeSelectionType } from '#domain/core/filterDefinition/types/treeSelection.type';
import type { InputSearchModelType } from '@c4-links/vue3-links-components';
import type { FilterDefinitionValue } from '@carrefour-gcs/bff-sdk';
import { computed, ref, watch, type Ref } from 'vue';
import { hasSearch, mergeTreeOptions, pruneTreeOptionsToSelections } from './utils/searchMandatoryTreeOption.utils';

/**
 * The values of a search-mandatory tree filter are not preloaded: this composable holds the options
 * of the selected nodes (fetched once, then completed from the search results), so they are displayed
 * without search, and the search results while searching.
 */
export function useSearchMandatoryTreeOptions(deps: {
  isEnabled: Ref<boolean>;
  initialValue: Ref<unknown>;
  searchInput: Ref<InputSearchModelType | undefined>;
  searchData: Readonly<Ref<FilterDefinitionValue[] | undefined>>;
  fetchValuesByKeys: (keys: string[], opts?: { paginated?: boolean }) => Promise<FilterDefinitionValue[]>;
}) {
  const selectedOptions = ref<FilterDefinitionValue[]>([]);
  const isSearching = computed(() => hasSearch(deps.searchInput.value));

  const options = computed(() =>
    isSearching.value ? mergeTreeOptions(selectedOptions.value, deps.searchData.value ?? []) : selectedOptions.value
  );

  /**
   * Fetches the selected nodes: the last key of a path identifies the selected node.
   */
  const initialize = async () => {
    const selections = (Array.isArray(deps.initialValue.value) ? deps.initialValue.value : []) as TreeSelectionType[];
    const keys = [...new Set(selections.flatMap((selection) => selection.path?.at(-1)?.key ?? []))];

    if (!deps.isEnabled.value || keys.length === 0) {
      return;
    }

    try {
      const fetched = await deps.fetchValuesByKeys(keys, { paginated: false });
      selectedOptions.value = pruneTreeOptionsToSelections(fetched, selections);
    } catch (error) {
      console.error('[useSearchMandatoryTreeOptions] Error fetching selected values:', error);
    }
  };

  /**
   * Keeps the selected nodes in sync with the selection, completing them from the displayed search results.
   */
  const onSelectionChange = (selections: TreeSelectionType[]) => {
    if (!deps.isEnabled.value) {
      return;
    }

    selectedOptions.value = pruneTreeOptionsToSelections(options.value, selections);
  };

  watch(deps.isEnabled, (isEnabled) => isEnabled && initialize(), { immediate: true });

  return { options, onSelectionChange };
}
